import { readZip } from './zipReader';
import { readConstants } from './jsLiteral';
import { legacyToProcess, processBlock } from './klickprozessModel';

/**
 * Turns an interactive page - a zip holding one self-contained HTML page, or
 * the .html itself - into a Markdown article.
 *
 * Nothing of the page is executed: DOMParser builds an inert document, and
 * script data is read by jsLiteral.js. A "Klickprozess" export (its script
 * declares the elements `N`, their texts `X` and the arrows `K`) becomes a
 * ":::prozess" block - the same clickable diagram with its explanations,
 * drawn by the process plugin (plugins/process-tool). Any other page is converted
 * from its visible HTML.
 *
 * Returns the draft the editor takes (import/importHandoff.js) plus what was
 * found: `{ title, summary, text, files: [], warnings, kind, count }`.
 */

const HTML_FILE = /\.html?$/i;
// Files an export carries for people, not for the page.
const NOTES = /(^|\/)(version|readme|changelog|license)[^/]*\.(txt|md)$/i;

const clean = (text = '') => text.replace(/\s+/g, ' ').trim();

/** The page's HTML and name, from a zip or the .html itself. */
async function readPage(file, warnings) {
  if (HTML_FILE.test(file.name)) {
    return { html: await file.text(), name: file.name };
  }

  const entries = await readZip(file);
  const paths = [...entries.keys()].filter((path) => !path.startsWith('__MACOSX/'));
  const pages = paths
    .filter((path) => HTML_FILE.test(path))
    .sort((a, b) => {
      const isIndex = (path) => /(^|\/)index\.html?$/i.test(path);
      const depth = (path) => path.split('/').length;
      return (isIndex(b) - isIndex(a)) || (depth(a) - depth(b)) || a.localeCompare(b);
    });

  if (pages.length === 0) {
    throw new Error('noPage');
  }

  const [name] = pages;
  const ignored = paths.filter((path) => path !== name && !NOTES.test(path));
  if (ignored.length) {
    warnings.push({ key: 'ignoredFiles', values: { count: ignored.length, files: ignored.slice(0, 5).join(', ') } });
  }

  return { html: await (await entries.get(name).read()).text(), name };
}

/* ---------- Klickprozess ---------- */

const KINDS = { t: 'task', g: 'gateway', s: 'start', e: 'end' };

function isKlickprozess(data) {
  return data.N && typeof data.N === 'object' && Array.isArray(data.K);
}

/** Elements in the order the process runs: from the start along the arrows, the rest after. */
function processOrder(nodes, edges) {
  const ids = Object.keys(nodes);
  const start = ids.find((id) => nodes[id].k === 's') ?? ids[0];
  const order = [];
  const queue = [start];

  while (queue.length) {
    const id = queue.shift();
    if (order.includes(id) || !nodes[id]) continue;
    order.push(id);
    edges.filter((edge) => edge[0] === id).forEach((edge) => queue.push(edge[1]));
  }

  return [...order, ...ids.filter((id) => !order.includes(id))];
}

const number = (value) => (Number.isFinite(Number(value)) ? Number(value) : null);

/** The drawing's size and lanes, from the page's static SVG (svg#plan, rect.bahn, .bahnname). */
function readLayout(doc) {
  const svg = doc.querySelector('svg#plan') ?? doc.querySelector('svg');
  const [, , width, height] = (svg?.getAttribute('viewBox') ?? '').split(/[\s,]+/).map(number);
  const names = [...(svg?.querySelectorAll('.bahnname') ?? [])].map((el) => clean(el.textContent));
  const lanes = [...(svg?.querySelectorAll('rect.bahn') ?? [])]
    .map((rect, index) => ({
      name: names[index] ?? '',
      y: number(rect.getAttribute('y')),
      height: number(rect.getAttribute('height')),
    }))
    .filter((lane) => lane.y !== null && lane.height !== null);

  return { width: width ?? null, height: height ?? null, lanes };
}

/** The page's process in the Klickprozess shape, converted by legacyToProcess (import/klickprozessModel.js). */
function klickprozessData(doc, data) {
  const raw = JSON.parse(JSON.stringify(data.N));

  // As the page does: X holds [title, explanation, steps, note] per element.
  for (const [id, entry] of Object.entries(data.X ?? {})) {
    if (!raw[id] || !Array.isArray(entry)) continue;
    const [title, text, steps, note] = entry;
    if (title) raw[id].n = `Aufgabe ${id}: ${title}`;
    if (text) raw[id].d = text;
    raw[id].l = steps;
    raw[id].h = note;
  }

  const edges = data.K.filter((edge) => Array.isArray(edge) && raw[edge[0]] && raw[edge[1]]);
  const order = processOrder(raw, edges);
  const layout = readLayout(doc);

  const nodes = order.map((id) => {
    const node = raw[id];
    return {
      id,
      kind: KINDS[node.k] ?? 'task',
      x: number(node.x),
      y: number(node.y),
      lane: number(node.b) ?? 1,
      label: node.k === 't' ? `Aufgabe ${id}` : id,
      title: clean(node.n) || `Aufgabe ${id}`,
      text: clean(node.d),
      steps: (Array.isArray(node.l) ? node.l : []).map(clean).filter(Boolean),
      hint: clean(node.h),
    };
  });

  const maxX = Math.max(...nodes.map((node) => node.x ?? 0));
  const maxY = Math.max(...nodes.map((node) => node.y ?? 0));

  return {
    title: clean(doc.querySelector('#klappe span:nth-of-type(2)')?.textContent)
      || clean(doc.querySelector('h1')?.textContent)
      || clean(doc.title),
    width: layout.width ?? maxX + 60,
    height: layout.height ?? maxY + 60,
    lanes: layout.lanes,
    nodes,
    edges: edges.map(([from, to, points, label, labelX, labelY]) => ({
      from,
      to,
      points: String(points ?? '').trim().split(/\s+/).map((pair) => pair.split(',').map(Number)),
      ...(label ? { label: clean(label), labelX: number(labelX), labelY: number(labelY) } : {}),
    })),
  };
}

function klickprozessMarkdown(doc, data) {
  const process = klickprozessData(doc, data);
  const parts = [];

  const intro = clean(doc.querySelector('.einleitung')?.textContent);
  if (intro) parts.push(intro);

  // Stored in the process plugin's model, which draws it (import/klickprozessModel.js).
  parts.push(processBlock(legacyToProcess(process)));

  const footnotes = [...doc.querySelectorAll('.fuss')].map((p) => clean(p.textContent)).filter(Boolean);
  if (footnotes.length) {
    parts.push(footnotes.map((note) => `*${note}*`).join('\n\n'));
  }

  const start = process.nodes.find((node) => node.kind === 'start');
  const end = [...process.nodes].reverse().find((node) => node.kind === 'end');

  return {
    title: process.title.replace(/^Prozess:\s*/i, '') || process.title,
    summary: start && end
      ? `Klickprozess mit ${process.nodes.length} Elementen: von „${start.title}“ bis „${end.title}“.`
      : '',
    text: parts.join('\n\n'),
    kind: 'klickprozess',
    count: process.nodes.length,
  };
}

/* ---------- any other page ---------- */

const SKIP = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'SVG', 'svg', 'CANVAS', 'IFRAME', 'BUTTON', 'NAV', 'FORM', 'HEAD']);

function inline(node) {
  if (node.nodeType === 3) return node.textContent.replace(/\s+/g, ' ');
  if (node.nodeType !== 1 || SKIP.has(node.tagName) || node.hidden) return '';
  const inner = [...node.childNodes].map(inline).join('');
  switch (node.tagName) {
    case 'STRONG': case 'B': return inner.trim() ? `**${inner.trim()}**` : '';
    case 'EM': case 'I': return inner.trim() ? `*${inner.trim()}*` : '';
    case 'CODE': return inner ? `\`${inner}\`` : '';
    case 'BR': return '\n';
    case 'A': {
      const href = node.getAttribute('href') ?? '';
      return /^https?:\/\//i.test(href) && inner.trim() ? `[${inner.trim()}](${href})` : inner;
    }
    default: return inner;
  }
}

function blocks(node, parts) {
  for (const child of node.children) {
    if (SKIP.has(child.tagName) || child.hidden) continue;
    const tag = child.tagName;
    const text = clean(inline(child));

    if (/^H[1-6]$/.test(tag)) {
      if (text) parts.push(`${'#'.repeat(Math.min(Math.max(Number(tag[1]) + 1, 2), 4))} ${text}`);
    } else if (tag === 'P' || tag === 'FIGCAPTION' || tag === 'SUMMARY') {
      if (text) parts.push(text);
    } else if (tag === 'UL' || tag === 'OL') {
      const items = [...child.children].filter((li) => li.tagName === 'LI').map((li) => clean(inline(li))).filter(Boolean);
      if (items.length) parts.push(items.map((item, index) => (tag === 'OL' ? `${index + 1}. ${item}` : `- ${item}`)).join('\n'));
    } else if (tag === 'BLOCKQUOTE') {
      if (text) parts.push(`> ${text}`);
    } else if (tag === 'TABLE') {
      const rows = [...child.querySelectorAll('tr')].map((tr) => [...tr.children].map((cell) => clean(inline(cell)).replace(/\|/g, '\\|')));
      if (rows.length) {
        const width = Math.max(...rows.map((row) => row.length));
        const line = (row) => `| ${Array.from({ length: width }, (_, i) => row[i] ?? '').join(' | ')} |`;
        parts.push([line(rows[0]), line(Array(width).fill('---')), ...rows.slice(1).map(line)].join('\n'));
      }
    } else if (tag === 'HR') {
      parts.push('---');
    } else if (child.children.length) {
      blocks(child, parts);
    } else if (text) {
      parts.push(text);
    }
  }
}

function genericMarkdown(doc, warnings) {
  const parts = [];
  blocks(doc.body ?? doc.documentElement, parts);

  if (doc.querySelector('img')) warnings.push({ key: 'imagesSkipped' });
  if (doc.querySelector('script')) warnings.push({ key: 'scriptsSkipped' });

  const title = clean(doc.querySelector('h1')?.textContent) || clean(doc.title);
  // The page's own main heading becomes the article title, not a section.
  if (parts[0]?.replace(/^#+\s*/, '') === title) parts.shift();

  return { title, summary: '', text: parts.join('\n\n'), kind: 'page', count: parts.length };
}

/* ---------- entry point ---------- */

export async function importHtmlPage(file) {
  const warnings = [];
  const { html, name } = await readPage(file, warnings);
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const meta = clean(doc.querySelector('meta[name="description"]')?.getAttribute('content'));

  const script = [...doc.querySelectorAll('script:not([src])')].map((el) => el.textContent).join('\n;\n');
  const data = script ? readConstants(script) : {};

  const result = isKlickprozess(data) ? klickprozessMarkdown(doc, data) : genericMarkdown(doc, warnings);

  if (!result.text.trim()) {
    throw new Error('noContent');
  }

  return {
    ...result,
    title: result.title || name.split('/').pop().replace(HTML_FILE, ''),
    summary: meta || result.summary,
    files: [],
    warnings,
  };
}
